import {annualTotal,type BillingCycle} from "@/lib/billing";
import BillingOptions,{AccessFeatures} from '@/components/feature/BillingOptions';
import AccountPackages from "@/components/feature/panel/AccountPackages";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePackages } from "@/hooks/useAdmin";
import { useMySubscription, useMyPayments, PAYMENT_STATUS_LABELS, PAYMENT_STATUS_COLORS } from "@/hooks/usePayments";
import { usePackageUsage } from "@/hooks/usePackageUsage";
import PurchaseModal from "./components/PurchaseModal";
import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";

function formatPrice(value: number): string {
  return `₺${value.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function Paketim() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: packages, loading: pkgLoading, error: pkgError, reload: pkgReload } = usePackages();
  const { data: subscription, reload: subReload } = useMySubscription();
  const { data: payments, reload: payReload } = useMyPayments();
  const { data: usage, loading, error, reload } = usePackageUsage();
  const [cycle,setCycle]=useState<BillingCycle>(searchParams.get('billing')==='yearly'?'yearly':'monthly');
  const [buying, setBuying] = useState<{ id: string; name: string; price: number; billingCycle:BillingCycle } | null>(null);

  const payResult = searchParams.get("odeme");

  const sortedPkgs = useMemo(
    () => (packages ?? []).filter((p) => p.active).sort((a, b) => a.sortOrder - b.sortOrder),
    [packages],
  );


  const currentPkg = useMemo(() => {
    if (subscription?.packageId) {
      return sortedPkgs.find((p) => p.id === subscription.packageId || p.productCode === subscription.packageId || p.name.trim().toLocaleLowerCase("tr-TR") === subscription.packageName.trim().toLocaleLowerCase("tr-TR")) ?? null;
    }
    return null;
  }, [sortedPkgs, subscription]);

  const currentSort = currentPkg?.sortOrder ?? 0;

  const dismissResult = () => {
    searchParams.delete("odeme");
    setSearchParams(searchParams, { replace: true });
  };

  const handlePurchased = () => {
    subReload();
    payReload();
  };

  return (
    <div className="space-y-8">
      <div className="rounded-lg border p-4"><strong>Kurumunuzdan eğitim paketi mi aldınız?</strong><p className="mt-2"><Link to="/kurum/aktivasyon" className="underline">Kurum aktivasyon kodunuzu kullanın</Link></p></div>
      {/* Ödeme sonucu bildirimi */}
      {payResult && (
        <div
          className={`rounded-lg border px-4 py-3 flex items-center justify-between gap-3 ${
            payResult === "basarili"
              ? "border-accent-300 bg-accent-100/70 text-accent-900"
              : "border-red-200 bg-red-50 text-red-700"
          }`}
        >
          <div className="flex items-center gap-2 text-sm font-medium">
            <i className={payResult === "basarili" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} aria-hidden="true"></i>
            {payResult === "basarili"
              ? "Ödemen başarıyla alındı. Paketin aktifleştirildi!"
              : "Ödeme tamamlanamadı. Lütfen tekrar dene."}
          </div>
          <button
            type="button"
            onClick={dismissResult}
            className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-background-100 transition-colors"
            aria-label="Bildirimi kapat"
          >
            <i className="ri-close-line" aria-hidden="true"></i>
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">Paketim</h1>
          <p className="text-sm md:text-base text-foreground-600 mt-1">
            Aktif paketin, ödemelerin ve kullanım durumun.
          </p>
        </div>
      </div>

      <AccountPackages />

      {/* Kullanım */}
      <section>
        <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-4">Kullanım Durumun</h2>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {(usage ?? []).map((item) => (
              <div key={item.label} className="bg-background-50 rounded-xl border border-background-200 p-5">
                <p className="text-sm text-foreground-600 mb-2">{item.label}</p>
                <p className="text-xl font-bold text-foreground-950">
                  {item.used}
                  {item.total !== "Sınırsız" && (
                    <span className="text-sm font-normal text-foreground-400"> / {item.total}</span>
                  )}
                </p>
                {item.percent > 0 && (
                  <div className="mt-3 h-2 rounded-full bg-background-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary-500 transition-all"
                      style={{ width: `${item.percent}%` }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Paket seçenekleri */}
      <section>
        <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-1">Paketler</h2>
        <p className="text-sm text-foreground-500 mb-4">
          Bir üst pakete geçmek için paketi seç ve ödeme adımına ilerle.
        </p>
        <BillingOptions cycle={cycle} onChange={setCycle}/>
        {pkgLoading ? (
          <LoadingState />
        ) : pkgError ? (
          <ErrorState message={pkgError} onRetry={pkgReload} />
        ) : sortedPkgs.length === 0 ? (
          <div className="rounded-lg border border-background-200 bg-background-50 p-8 text-center text-sm text-foreground-500">
            Şu anda satın alınabilecek paket bulunmuyor.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {sortedPkgs.map((pkg) => {
              const yearly=cycle==='yearly'&&pkg.annualEnabled&&pkg.price>0;
              const total=yearly?annualTotal(pkg.price,pkg.annualDiscountPercent):pkg.price;
              const isCurrent = currentPkg?.id === pkg.id;
              const isUpgrade = pkg.sortOrder > currentSort;
              const isDowngrade = pkg.sortOrder < currentSort;
              return (
                <div
                  key={pkg.id}
                  className={`rounded-xl border p-6 flex flex-col ${
                    isCurrent ? "border-primary-400 bg-primary-50" : "border-background-200 bg-background-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading font-semibold text-base text-foreground-950">{pkg.name}</h3>
                    {isCurrent && (
                      <span className="text-xs font-semibold bg-primary-500 text-background-50 px-2 py-0.5 rounded-full">
                        Mevcut
                      </span>
                    )}
                    {isUpgrade && (
                      <span className="text-xs font-semibold bg-accent-100 text-accent-800 px-2 py-0.5 rounded-full">
                        Üst Paket
                      </span>
                    )}
                  </div>
                  {pkg.description && (
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{pkg.description}</p>
                  )}
                  <p className="mt-3 text-2xl font-bold text-foreground-950">
                    {pkg.price === 0 ? "Ücretsiz" : formatPrice(total)}
                  </p>
                  <p className="text-xs text-foreground-500">
                    {pkg.price === 0 ? "Süresiz erişim" : (yearly?'12 ay erişim · peşin':'1 ay erişim')}
                  </p>
                  {pkg.price>0&&pkg.annualEnabled&&<p className="my-3 text-sm text-primary-700">12 aylık peşin ödemeye %{pkg.annualDiscountPercent} indirim{yearly&&<> · <del>{formatPrice(pkg.price*12)}</del> yerine {formatPrice(total)}</>}</p>}
                  <AccessFeatures merakli={pkg.merakliEnabled} meeting={pkg.meetingEnabled} voice={pkg.voiceCoach} parental={pkg.parentalControls}/>
                  <ul className="mt-4 space-y-2.5 flex-1">
                    {(pkg.features ?? []).map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm text-foreground-700">
                        <i className="ri-check-line text-primary-600 mt-0.5" aria-hidden="true"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    disabled={pkg.price===0 || isDowngrade}
                    onClick={() => setBuying({ id: pkg.id, name: pkg.name, price: total, billingCycle: yearly?"yearly":"monthly" })}
                    className={`mt-5 w-full h-10 rounded-md text-sm font-semibold transition-colors whitespace-nowrap ${
                      isCurrent && pkg.price===0
                        ? "bg-background-100 text-foreground-400 cursor-default"
                        : isDowngrade
                          ? "bg-background-100 text-foreground-400 cursor-not-allowed"
                          : "bg-primary-500 text-background-50 hover:bg-primary-600"
                    }`}
                  >
                    {pkg.price===0 ? "Ücretsiz Paket" : isCurrent ? "Süreyi Uzat" : isDowngrade ? "Geçilemez" : "Yükselt"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Ödeme geçmişi */}
      {payments && payments.length > 0 && (
        <section>
          <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-4">Ödeme Geçmişi</h2>
          <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/60">
                  <th className="px-4 py-3 text-left font-semibold text-foreground-700">Paket</th>
                  <th className="px-4 py-3 text-left font-semibold text-foreground-700">Tutar</th>
                  <th className="px-4 py-3 text-left font-semibold text-foreground-700">Tarih</th>
                  <th className="px-4 py-3 text-left font-semibold text-foreground-700">Durum</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-b border-background-200 hover:bg-background-100/40 transition-colors">
                    <td className="px-4 py-3 font-medium text-foreground-950">{p.packageName || "—"}</td>
                    <td className="px-4 py-3 text-foreground-900 font-semibold whitespace-nowrap">{formatPrice(p.amount)}</td>
                    <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{p.createdAt}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${PAYMENT_STATUS_COLORS[p.status] ?? "bg-background-100 text-foreground-600"}`}>
                        {PAYMENT_STATUS_LABELS[p.status] ?? p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <PurchaseModal
        open={!!buying}
        onClose={() => setBuying(null)}
        pkg={buying}
        onPurchased={handlePurchased}
      />
    </div>
  );
}
